import { Box, Stack, Tab, styled } from "@mui/material";
import {
  Suspense,
  SyntheticEvent,
  lazy,
  memo,
  useEffect,
  useMemo,
} from "react";
import ProductsTab from "./ProductsTab";
import { useSupplierContext } from "..";
import useNavigate from "hooks/useNavigate";
import { SEARCH_PARAMS } from "constants/routes";
import { objectToQueryString } from "helpers/search";
import { useTranslation } from "hooks/useTranslation";
import PaidTabs from "Components/tab/PaidTabs";
import ContactUsTab from "./ContactUsTab";
import FreshdiLoading from "Components/loading/FreshdiLoading";

const Posts = lazy(() => import("./Posts"));
const ReviewTab = lazy(() => import("./ReviewTab"));
const PaidOverviewTab = lazy(() => import("./PaidOverviewTab"));
const HomeTab = lazy(() => import("./HomeTab"));

const PaidSupplierTabs = () => {
  const t = useTranslation();
  const { history, search } = useNavigate();
  const { setSelectedTab, selectedTab, postData, supplierDetail } =
    useSupplierContext();
  const tabs = useMemo(
    () => [
      {
        label: "home",
        value: "home",
        hide: supplierDetail?.is_landing_page !== 1,
      },
      { label: "company_overview", value: "" },
      { label: "products", value: "product" },
      {
        label: "post",
        value: "post",
        hide: (postData?.data || []).length < 1,
      },
      { label: "review", value: "review" },
      { label: "contact_us", value: "contact" },
    ],
    [postData, supplierDetail]
  );

  const onChangeTab = (event: SyntheticEvent, value: string) => {
    history.replace({
      search: objectToQueryString({
        [SEARCH_PARAMS.tab]: value || undefined,
      }),
    });
  };

  const ContentTab = useMemo(() => {
    switch (selectedTab) {
      case "product":
        return ProductsTab;

      case "post":
        return Posts;

      case "review":
        return ReviewTab;

      case "contact":
        return ContactUsTab;

      case "home":
        return HomeTab;

      default:
        return PaidOverviewTab;
    }
  }, [selectedTab]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const tabParam = params.get(SEARCH_PARAMS.tab);
    setSelectedTab(tabParam || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <StyledSupplierTabs>
      <PaidTabs
        value={selectedTab}
        onChange={onChangeTab}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs
          .filter((tab) => !tab.hide)
          .map(({ label, value }) => (
            <Tab key={value} {...{ value }} label={t(label)} />
          ))}
      </PaidTabs>

      <Stack className="MuiTabs-root" mt={1}>
        <Suspense fallback={<FreshdiLoading />}>
          <ContentTab />
        </Suspense>
      </Stack>
    </StyledSupplierTabs>
  );
};

const StyledSupplierTabs = styled(Box)(({ theme }) => ({
  ".MuiTabs-root": {
    padding: "0 120px",
  },

  [theme.breakpoints.down("md")]: {
    ".MuiTabs-root": {
      padding: "0",
    },
  },
}));

export default memo(PaidSupplierTabs);
